import React from 'react';
import {Box} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import {CIBLE_REMPLISSAGE, NAVY, TEXTE_SECONDAIRE} from '../../tokens.js';
import Courbe from '../Courbe.js';
import TuileKpi from '../TuileKpi.js';
import DashAlertes from './DashAlertes.js';
import {DashVide} from './DashEtats.js';
import {Communes, Patrimoines} from './DashRepartitions.js';
import {formatNombre, formatPct, formatRse, formatTonnes} from './dashboard.selectors.js';
import {carteSx, enteteSectionSx, sousTitreSx, titreSectionSx} from './dashStyles.js';

// PIL-01 lot 9 : le dashboard en trois bandeaux (un bandeau par question). Tuiles, alertes et tableaux existants sont
// réutilisés ; ce fichier ne fait que les assembler.

const GRILLE = (n) => ({display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: '16px'});

export function Bandeau({titre, id, children}) {
    return (
        <Box component="section" aria-labelledby={id} sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <Box component="h2" id={id} sx={{...titreSectionSx, fontSize: 12}}>{titre}</Box>
            {children}
        </Box>
    );
}

/** Tuile KPI cliquable (lien carte) ou statique. */
function Tuile({lien, ...props}) {
    const navigate = useNavigate();
    return <TuileKpi {...props} onClick={lien ? () => navigate(lien) : undefined}/>;
}

function CarteCourbe({titre, sousTitre, children}) {
    return (
        <Box sx={{...carteSx, p: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
            <Box sx={{display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap'}}>
                <Box component="h3" sx={{...titreSectionSx, fontSize: 12}}>{titre}</Box>
                {sousTitre && <Box component="span" sx={sousTitreSx}>{sousTitre}</Box>}
            </Box>
            {children}
        </Box>
    );
}

const formatKg = (kg) => (kg == null ? null : `${Math.round(kg).toLocaleString('fr-FR')} kg`);

/** A1 : service de lavage. */
export function BandeauLavage({colonnes, rse, courbe, tuileImpossibles, lienImpossibles, liens, vide = false, videProps = {}}) {
    const l = colonnes.lavage;
    const f = formatRse(rse);
    if (vide) {
        // Période sans événement : message existant, puis l'alerte à date.
        return (
            <Bandeau titre="Service de lavage" id="bandeau-lavage">
                <DashVide {...videProps}/>
                {tuileImpossibles && <DashAlertes tuiles={[tuileImpossibles]} liens={lienImpossibles} colonnes={1} label="Alerte lavage"/>}
            </Bandeau>
        );
    }
    return (
        <Bandeau titre="Service de lavage" id="bandeau-lavage">
            <Box sx={GRILLE(5)}>
                <Tuile chiffre={formatPct(l.tauxService)} libelle="Taux de service" sousLigne={`${formatNombre(l.laves)} PAV lavés sur ${formatNombre(l.parc)}`} progression={l.tauxService} lien={liens.lavage}/>
                <Tuile chiffre={l.realises} libelle="Lavages réalisés" lien={liens.lavage}/>
                <Tuile chiffre={l.impossibles} libelle="Passages impossibles" lien={liens.impossibles}/>
                <Tuile chiffre={f.eauConsommee} libelle="Eau consommée" sousLigne="typologie de lavage par borne"/>
                <Tuile chiffre={f.km} libelle="Kilomètres parcourus" sousLigne="moyenne par tournée, extrapolée"/>
            </Box>
            <Box sx={{display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '16px', alignItems: 'stretch'}}>
                <CarteCourbe titre="Lavages réalisés par semaine" sousTitre="sur la période">
                    <Courbe points={courbe} couleur={NAVY} format={(y) => formatNombre(y)}/>
                </CarteCourbe>
                {tuileImpossibles && <DashAlertes tuiles={[tuileImpossibles]} liens={lienImpossibles} colonnes={1} label="Alerte lavage"/>}
            </Box>
        </Bandeau>
    );
}

/** A2 : état du parc (à la période pour les tuiles, à date pour les alertes). */
export function BandeauParc({colonnes, elementN1, condamnees, repartitions, tuilesAlertes, liensAlertes, lienCommune, lienPatrimoine, liens}) {
    const m = colonnes.maintenance;
    return (
        <Bandeau titre="État du parc" id="bandeau-parc">
            <Box sx={GRILLE(4)}>
                <Tuile chiffre={formatPct(m.couverture)} libelle="Couverture maintenance" sousLigne={`${formatNombre(m.inspectes)} PAV inspectés sur ${formatNombre(m.parc)}`} progression={m.couverture} lien={liens.maintenance}/>
                <Tuile chiffre={m.enDefaut} libelle="En défaut" sousLigne={`élément n°1 : ${elementN1 ? elementN1.libelle : '—'}`} lien={liens.enDefaut}/>
                <Tuile chiffre={colonnes.graffitis.constates} libelle="Graffitis constatés" sousLigne="PAV avec graffiti constaté" lien={liens.graffitis}/>
                <Tuile chiffre={condamnees} libelle="Bornes condamnées" sousLigne="à date · dernier passage impossible" lien={liens.condamnees}/>
            </Box>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '24px', alignItems: 'start'}}>
                <Communes communes={repartitions.communes} lienCommune={lienCommune}/>
                <Patrimoines lignes={repartitions.lignes} lienPatrimoine={lienPatrimoine}/>
            </Box>
            <DashAlertes tuiles={tuilesAlertes} liens={liensAlertes} colonnes={3} label="Alertes sur le patrimoine"/>
        </Bandeau>
    );
}

const TH = {fontWeight: 600, p: '10px 12px', borderBottom: '1px solid #EEF1F4', textAlign: 'right', backgroundColor: '#FFFFFF', position: 'sticky', top: 0};
const TD = {p: '12px', borderBottom: '1px solid #EEF1F4', textAlign: 'right'};

function TableauQuartiers({lignes, lienCommune}) {
    const navigate = useNavigate();
    return (
        <Box component="section" aria-labelledby="quartiers-title" sx={carteSx}>
            <Box sx={enteteSectionSx}>
                <Box component="h3" id="quartiers-title" sx={titreSectionSx}>Collecte par commune</Box>
                <Box component="span" sx={sousTitreSx}>commune · PAV · tonnage · remplissage moyen · déclassés · kg / 100 hab. alloués · PAV > 250 hab. alloués à 150 m · tri par remplissage croissant</Box>
            </Box>
            <Box sx={{overflow: 'auto', maxHeight: 360}}>
                <Box component="table" sx={{width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13}}>
                    <thead>
                        <Box component="tr" sx={{color: TEXTE_SECONDAIRE, fontSize: 12}}>
                            <Box component="th" scope="col" sx={{...TH, textAlign: 'left', pl: '24px'}}>Commune</Box>
                            <Box component="th" scope="col" sx={TH}>PAV</Box>
                            <Box component="th" scope="col" sx={TH}>Tonnage</Box>
                            <Box component="th" scope="col" sx={TH}>Remplissage moyen</Box>
                            <Box component="th" scope="col" sx={TH}>Déclassés</Box>
                            <Box component="th" scope="col" sx={TH} title="Tonnage de la période / population allouée des PAV de la commune x 100">kg / 100 hab.</Box>
                            <Box component="th" scope="col" sx={{...TH, pr: '24px'}} title="Grille rurale : PAV du parc aérien auxquels sont allouées plus de 250 habitants, à rayon fixe 150 m (indépendant du curseur)">PAV &gt; 250 hab. (150 m)</Box>
                        </Box>
                    </thead>
                    <tbody>
                        {lignes.map((l) => {
                            const total = l.cle === 'territoire';
                            const cell = total ? {...TD, fontWeight: 700, borderBottom: 'none', position: 'sticky', bottom: 0, backgroundColor: '#FFFFFF', borderTop: '1px solid #EEF1F4'} : TD;
                            const lien = lienCommune(total ? null : l.cle);
                            return (
                                <Box component="tr" key={l.cle} onClick={() => navigate(lien)} sx={{cursor: 'pointer', '&:hover td': {backgroundColor: '#F6F8F4'}}}>
                                    <Box component="td" sx={{...cell, textAlign: 'left', pl: '24px'}}>
                                        {total ? l.commune : <Box component={Link} to={lien} onClick={(e) => e.stopPropagation()} sx={{fontWeight: 600, color: NAVY}}>{l.commune}</Box>}
                                    </Box>
                                    <Box component="td" sx={cell}>{formatNombre(l.pav)}</Box>
                                    <Box component="td" sx={cell}>{formatTonnes(l.tonnageKg)}</Box>
                                    <Box component="td" sx={cell}>{formatPct(l.remplissage)}</Box>
                                    <Box component="td" sx={cell}>{formatNombre(l.declasses)}</Box>
                                    <Box component="td" sx={cell}>{l.kgPour100hab == null ? '—' : l.kgPour100hab.toLocaleString('fr-FR', {maximumFractionDigits: 1})}</Box>
                                    <Box component="td" sx={{...cell, pr: '24px'}}>{formatNombre(l.pavAuDessusSeuilRural)}</Box>
                                </Box>
                            );
                        })}
                    </tbody>
                </Box>
            </Box>
        </Box>
    );
}

/** A3 : collecte (bouton Collecte à 1). `vide` : aucune collecte sur la période. */
export function BandeauCollecte({synthese, vide, courbeTonnage, courbeRemplissage, quartiers, lienCommune, tuilesAlertes, liensAlertes, lienCollecte, lienDeclasses}) {
    return (
        <Bandeau titre="Collecte" id="bandeau-collecte">
            {vide ? (
                <Box role="status" sx={{...carteSx, p: '32px', textAlign: 'center', color: TEXTE_SECONDAIRE, fontSize: 14}}>Aucune collecte sur la période</Box>
            ) : (
                <>
                    <Box sx={GRILLE(4)}>
                        <Tuile chiffre={formatTonnes(synthese.tonnageKg)} libelle="Tonnage collecté" sousLigne="sur la période" lien={lienCollecte}/>
                        <Tuile chiffre={synthese.tonnageMoyenKg == null ? null : formatKg(synthese.tonnageMoyenKg)} libelle="Tonnage moyen par bac" lien={lienCollecte}/>
                        <Tuile chiffre={formatPct(synthese.remplissageMoyen)} libelle="Remplissage moyen" sousLigne={`cible ${formatPct(CIBLE_REMPLISSAGE)}`} lien={lienCollecte}/>
                        <Tuile chiffre={synthese.declassesEnCours} libelle="Déclassés en cours" sousLigne="dernière collecte déclassée" lien={lienDeclasses ?? lienCollecte}/>
                    </Box>
                    <Box sx={GRILLE(2)}>
                        <CarteCourbe titre="Tonnage par semaine" sousTitre="t / semaine">
                            <Courbe points={courbeTonnage} couleur="#7A4B2A" format={(kg) => formatTonnes(kg)}/>
                        </CarteCourbe>
                        <CarteCourbe titre="Remplissage moyen par semaine" sousTitre={`cible ${formatPct(CIBLE_REMPLISSAGE)} en pointillé`}>
                            <Courbe points={courbeRemplissage} couleur="#7A4B2A" format={(y) => formatPct(y)} cible={CIBLE_REMPLISSAGE}/>
                        </CarteCourbe>
                    </Box>
                    <TableauQuartiers lignes={quartiers} lienCommune={lienCommune}/>
                </>
            )}
            <DashAlertes tuiles={tuilesAlertes} liens={liensAlertes} colonnes={3} label="Alertes collecte"/>
        </Bandeau>
    );
}
